import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Building2, MapPin, Users, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const PLAN_STYLES = {
  starter: "bg-slate-100 text-slate-600",
  growth: "bg-blue-50 text-blue-700",
  enterprise: "bg-indigo-50 text-indigo-700",
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export default function Tenants() {
  const [societies, setSocieties] = useState(null);

  useEffect(() => {
    api
      .get("/societies")
      .then((r) => setSocieties(r.data))
      .catch((e) => {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        setSocieties([]);
      });
  }, []);

  const stats = useMemo(() => {
    if (!societies) return { total: 0, units: 0, cities: 0 };
    return {
      total: societies.length,
      units: societies.reduce((s, x) => s + (x.units || 0), 0),
      cities: new Set(societies.map((x) => x.city)).size,
    };
  }, [societies]);

  return (
    <div data-testid="tenant-management-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Platform Admin</p>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="tenants-heading">
            Tenant Management
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Every society registered on SynqSMC ERP — complete list, no filters applied.
          </p>
        </div>
        <span className="rounded-full bg-blue-50 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700" data-testid="tenants-total-badge">
          {stats.total} societies
        </span>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="tenants-stats">
        {[
          [Building2, stats.total, "Registered societies"],
          [Users, stats.units.toLocaleString("en-IN"), "Total units managed"],
          [MapPin, stats.cities, "Cities covered"],
        ].map(([Icon, val, label]) => (
          <div key={label} className="card-soft flex items-center gap-4 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <Icon size={20} />
            </span>
            <div>
              <p className="text-xl font-extrabold text-slate-900">{val}</p>
              <p className="text-xs text-slate-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="card-soft overflow-hidden" data-testid="tenants-table-card">
        {societies === null ? (
          <div className="flex items-center justify-center gap-3 p-16 text-slate-400" data-testid="tenants-loading">
            <Loader2 size={20} className="animate-spin" /> Loading societies…
          </div>
        ) : societies.length === 0 ? (
          <p className="p-16 text-center text-sm text-slate-400" data-testid="tenants-empty">
            No societies registered yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left" data-testid="tenants-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  {["Society", "Admin", "Units", "Plan", "Status", "Registered"].map((h) => (
                    <th key={h} className="px-5 py-3.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {societies.map((s) => (
                  <tr key={s.id} data-testid={`tenant-row-${s.id}`} className="border-b border-slate-50 transition-colors hover:bg-blue-50/40">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                          <Building2 size={16} />
                        </span>
                        <div>
                          <p className="text-sm font-bold text-slate-900">{s.name}</p>
                          <p className="text-xs text-slate-400">{s.address}, {s.city}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-sm font-medium text-slate-700">{s.admin_name}</p>
                      <p className="text-xs text-slate-400">{s.admin_email}</p>
                    </td>
                    <td className="px-5 py-4 text-sm font-semibold text-slate-700">{s.units}</td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider ${PLAN_STYLES[s.plan] || PLAN_STYLES.starter}`}>
                        {s.plan}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-500">{formatDate(s.registered_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
